import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Create axios instance with auth interceptor
const axiosAuth = axios.create();
axiosAuth.interceptors.request.use((config) => {
  const token = localStorage.getItem("id_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const api = {
  getMe: () => axiosAuth.get(`${API}/auth/me`),
  getStats: () => axiosAuth.get(`${API}/dashboard/my-stats`),
  getOrders: (params) => axiosAuth.get(`${API}/orders`, { params }),
  getOrdersCount: (params) => axiosAuth.get(`${API}/orders/count`, { params }),

  // Cross-reference uploaded IG follower scans with customer influencers —
  // auto-applies the built-in "Follows me" tag on exact handle matches.
  syncFollowsMe: () => axiosAuth.post(`${API}/sync-follows-me`),
  getInfluencers: (params) => axiosAuth.get(`${API}/influencers`, { params }),
  searchInfluencers: (query) => axiosAuth.get(`${API}/influencers`, { params: { search: query, limit: 10 } }),
  lookupNameCollision: (name) => axiosAuth.get(`${API}/name-collision/lookup`, { params: { name } }),
  getNameCollisionStatus: () => axiosAuth.get(`${API}/admin/name-collision/status`),
  triggerNameCollisionImport: () => axiosAuth.post(`${API}/admin/name-collision/import`),
  getInfluencersByIds: (ids) => axiosAuth.get(`${API}/influencers`, { params: { ids: ids.join(","), limit: 100 } }),
  getInfluencer: (id) => axiosAuth.get(`${API}/influencers/${id}`),
  getInfluencerOrders: (id) => axiosAuth.get(`${API}/influencers/${id}/orders`),
  getNotifications: (params) => axiosAuth.get(`${API}/notifications`, { params }),
  markNotificationRead: (id) => axiosAuth.put(`${API}/notifications/${id}/read`),
  markAllRead: (storeId) => axiosAuth.put(`${API}/notifications/read-all?store_id=${storeId}`),
  updateInfluencerStatus: (id, status) => axiosAuth.put(`${API}/influencers/${id}/status?status=${status}`),
  updateIdentityStatus: (id, status) => axiosAuth.put(`${API}/influencers/${id}/identity-status?status=${status}`),
  generateMessage: (data) => axiosAuth.post(`${API}/generate-message`, data),
  takeAction: (data) => axiosAuth.post(`${API}/actions/take`, data),
  seedDemo: () => axiosAuth.post(`${API}/seed-demo`),
  getStores: () => axiosAuth.get(`${API}/stores`),
  updateStoreSettings: (id, settings) => axiosAuth.put(`${API}/stores/${id}/settings`, settings),
  addSocialProfile: (influencerId, data) => axiosAuth.post(`${API}/influencers/${influencerId}/add-profile`, data),
  analyzeInfluencer: (influencerId) => axiosAuth.post(`${API}/influencers/${influencerId}/analyze`),

  // Order re-scan (recover stuck "Not detected" scans)
  rescanOrder: (orderId) => axiosAuth.post(`${API}/orders/${orderId}/rescan`),
  getOrderDetectionStatus: (orderId) => axiosAuth.get(`${API}/orders/${orderId}/detection-status`),
  startBatchImport: (data) => axiosAuth.post(`${API}/batch-import`, data),
  getBatchStatus: (jobId) => axiosAuth.get(`${API}/batch-import/${jobId}`),
  listBatchJobs: (params) => axiosAuth.get(`${API}/batch-import`, { params }),
  getShopifyStatus: () => axiosAuth.get(`${API}/shopify/status`),
  disconnectShopify: () => axiosAuth.post(`${API}/shopify/disconnect`),
  importShopifyOrders: () => axiosAuth.post(`${API}/shopify/import-orders`),
  getWoocommerceStatus: () => axiosAuth.get(`${API}/woocommerce/status`),
  disconnectWoocommerce: () => axiosAuth.post(`${API}/woocommerce/disconnect`),
  changePassword: (current_password, new_password) =>
    axiosAuth.post(`${API}/auth/change-password`, { current_password, new_password }),

  // Backfill (scanning historical orders)
  startBackfill: () => axiosAuth.post(`${API}/backfill/start`),
  getBackfillStatus: () => axiosAuth.get(`${API}/backfill/status`),
  uploadSubscribers: (formData) => axiosAuth.post(`${API}/subscriber-import`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  getSubscriberImportStatus: (jobId) => axiosAuth.get(`${API}/subscriber-import/${jobId}`),
  listSubscriberImports: () => axiosAuth.get(`${API}/subscriber-imports`),

  // Custom keyword auto-tagging (Premium)
  listKeywords: () => axiosAuth.get(`${API}/keywords`),
  createKeyword: (term, campaign = "") => axiosAuth.post(`${API}/keywords`, { term, campaign }),
  updateKeyword: (id, patch) => axiosAuth.patch(`${API}/keywords/${id}`, patch),
  deleteKeyword: (id) => axiosAuth.delete(`${API}/keywords/${id}`),
  rescanKeywords: () => axiosAuth.post(`${API}/keywords/rescan`),

  // Social Followers Scan
  uploadFollowerScan: (formData) => axiosAuth.post(`${API}/follower-scan`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  getFollowerScanStatus: (jobId) => axiosAuth.get(`${API}/follower-scan/${jobId}`),
  listFollowerScans: () => axiosAuth.get(`${API}/follower-scans`),
  forceCompleteScan: (jobId) => axiosAuth.post(`${API}/follower-scan/${jobId}/complete`),

  // Watchlist
  getWatchlist: (params) => axiosAuth.get(`${API}/watchlist`, { params }),
  addWatchlistEntry: (data) => axiosAuth.post(`${API}/watchlist`, data),
  addWatchlistBulk: (data) => axiosAuth.post(`${API}/watchlist/bulk`, data),
  uploadWatchlistCsv: (formData) => axiosAuth.post(`${API}/watchlist/bulk-csv`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  deleteWatchlistEntry: (id) => axiosAuth.delete(`${API}/watchlist/${id}`),
  getWatchlistStats: () => axiosAuth.get(`${API}/watchlist/stats`),
  getWatchlistUnviewedMatches: () => axiosAuth.get(`${API}/watchlist/unviewed-matches`),
  markWatchlistViewed: () => axiosAuth.post(`${API}/watchlist/mark-viewed`),

  // Outreach log — tracks DMs/emails sent per influencer
  logOutreach: (data) => axiosAuth.post(`${API}/outreach/log`, data),
  deleteOutreach: (targetId) => axiosAuth.delete(`${API}/outreach/log/${targetId}`),
  getContactedMap: () => axiosAuth.get(`${API}/outreach/contacted`),
  getOutreachHistory: (targetId) => axiosAuth.get(`${API}/outreach/log/${targetId}`),

  // Platform waitlist (for non-WooCommerce stores waiting for native integration)
  joinWaitlist: (data) => axiosAuth.post(`${API}/waitlist/join`, data),

  // Tags — organize customer influencers with built-in + custom labels
  getTags: () => axiosAuth.get(`${API}/tags`),
  addTag: (tag) => axiosAuth.post(`${API}/tags`, { tag }),
  deleteTag: (tag) => axiosAuth.delete(`${API}/tags/${encodeURIComponent(tag)}`),
  setInfluencerTags: (id, tags) => axiosAuth.put(`${API}/influencers/${id}/tags`, { tags }),

  // Auth + credit status (used by CreditsBanner)
  me: () => axiosAuth.get(`${API}/auth/me`),
};

export default api;
